import classNames from 'classnames'
import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { HeaderDataContext } from '../context/HeaderContext'
import { usePrevious } from '../hooks/usePrevious'
import { useTimer } from '../hooks/useTimer'
import { useWindowScroll } from '../hooks/useWindowScroll'
import { StickySubMenu, StickySubMenuProps } from './StickySubMenu'

export enum MenuVariant {
  Compact,
  PortfolioPage,
  PeoplePage,
}

export interface AnimatedStickySubMenuProps
  extends Omit<StickySubMenuProps, 'offset' | 'textClassName'> {
  anchorElement?: HTMLElement
  variant?: MenuVariant
}

export const AnimatedStickySubMenu: FC<AnimatedStickySubMenuProps> = ({
  additionalItemClassName,
  additionalStickyWrapperClassName = '',
  anchorElement,
  gaTrackingId,
  id,
  items,
  justifyListClassName,
  scrollOffset = 0,
  variant = MenuVariant.PortfolioPage,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const headerHeight = useContext(HeaderDataContext)
  const previousHeaderHeight = usePrevious(headerHeight) ?? 0
  const scrollYPosition = useWindowScroll()

  const [isSticky, setIsSticky] = useState(false)

  const animationTimer = useTimer()

  // This function takes care of animating the sticky menu width and position when it starts/stops being sticky
  const update = useCallback(
    (off: number) => {
      if (animationTimer.isRunning() === false && containerRef.current) {
        // Offset to start being sticky from the top of the screen/anchorElement
        const container = containerRef.current
        const rect = container.getBoundingClientRect()
        const anchorYPosition = Math.ceil(anchorElement?.getBoundingClientRect()?.y ?? 0)
        const offsetFromAnchor = anchorYPosition + off
        const shouldBeSticky = rect.top <= offsetFromAnchor
        setIsSticky(shouldBeSticky)
      }
    },
    [anchorElement, animationTimer]
  )

  /**
   * Default offset is header height -2px because Chrome will show ~1px of the header's progress bar
   * in sm and tb when using a high resolution display (rounding error)
   */
  const offset = headerHeight - 2

  // Pause setting isSticky (and therefore pause most animations) while the header height is animating
  useEffect(() => {
    animationTimer.start(() => update(offset), 250)
  }, [animationTimer, offset, update])

  // Run update when scrolling
  useEffect(() => {
    update(offset)
    // No need to run this effect every time the offset, ie the header height, changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollYPosition, update])

  // Run update when resizing
  useEffect(() => {
    const resize = () => {
      update(offset)
    }

    window.addEventListener('resize', resize, { passive: true })

    return () => {
      window.removeEventListener('resize', resize)
    }
  }, [offset, update])

  let height: string
  let textClasses: string

  switch (variant) {
    default:
    case MenuVariant.PortfolioPage:
      height = classNames({
        'h-75': isSticky,
        'h-75 sm:h-120 xl:h-90 xxl:h-120': !isSticky,
      })

      textClasses = isSticky
        ? classNames('font-t text-t-200', 'md:text-t-300', 'xl:text-t-500')
        : classNames(
            'font-t text-t-300',
            'sm:text-t-400',
            'md:text-t-500',
            'xl:text-t-600'
          )
      break

    case MenuVariant.Compact:
      height = classNames('h-75', {
        'sm:h-90': !isSticky,
      })

      textClasses = classNames('font-t text-t-300', 'sm:text-t-400', 'md:text-t-500')
      break

    case MenuVariant.PeoplePage:
      height = classNames('h-75', {
        'sm:h-150': !isSticky,
      })

      textClasses = isSticky
        ? classNames('font-t text-t-200', 'md:text-t-400', 'xl:text-t-500')
        : classNames('font-t text-t-300', 'sm:text-t-400', 'md:text-t-500')
  }

  let justifyClass = justifyListClassName
  if (!justifyListClassName && variant === MenuVariant.Compact) {
    justifyClass = 'justify-start'
  }

  return (
    <StickySubMenu
      additionalContentClassName={height}
      additionalItemClassName={additionalItemClassName}
      additionalStickyWrapperClassName={classNames(
        'h-75',
        additionalStickyWrapperClassName,
        {
          'duration-200': headerHeight <= previousHeaderHeight,

          // Animate the sticky sub menu slightly slower when the header is expanding to avoid scroll
          // progress bar flickering.
          'duration-300': headerHeight > previousHeaderHeight,
          'z-30': !isSticky,
          'z-navbar-sticky-sub-menu': isSticky,
        }
      )}
      backgroundWidthClassName={isSticky ? 'w-screen' : undefined}
      // Note: We switch the top-border-color when transitioning to/from sticky behavior. This seems to eliminate border flickering when
      // merging with the main header.
      borderColorClass={isSticky ? 'partial-border-color' : undefined}
      gaTrackingId={gaTrackingId}
      id={id}
      items={items}
      justifyListClassName={justifyClass}
      offset={offset}
      progressBarEnabled={isSticky}
      ref={containerRef}
      scrollOffset={scrollOffset}
      textClassName={classNames('transition-all duration-200', textClasses)}
    />
  )
}
